import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, channelName, Video } from '../lib/api'
import HudLoader from '../components/HudLoader'
import {
  Search,
  Edit,
  Trash2,
  Play,
  ShieldCheck,
  Loader2,
  Download,
  Upload,
  RefreshCw,
  Video as VideoIcon,
  Eye,
  Check,
  RotateCcw,
  X,
} from 'lucide-react'
import { format } from 'date-fns'

const STATUS_COLORS: Record<string, string> = {
  draft: 'badge-secondary',
  scripted: 'badge-info',
  voice_generated: 'badge-primary',
  visuals_generated: 'badge-primary',
  assembled: 'badge-primary',
  metadata_ready: 'badge-primary',
  compliance_passed: 'badge-success',
  compliance_failed: 'badge-danger',
  scheduled: 'badge-warning',
  uploading: 'badge-info',
  published: 'badge-success',
  failed: 'badge-danger',
  removed: 'badge-secondary',
}

const VISIBILITY_COLORS: Record<string, string> = {
  private: 'badge-secondary',
  unlisted: 'badge-info',
  public: 'badge-success',
}

export default function Videos() {
  const queryClient = useQueryClient()
  const [selectedChannel, setSelectedChannel] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  // Preview player (2.2.34): generated MP4 upload se pehle dekho.
  const [preview, setPreview] = useState<Video | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  // Local mp4 deploy wipe ho chuka (ya abhi bana nahi) par video YouTube
  // par uploaded hai — embed fallback (2.2.35).
  const [previewYt, setPreviewYt] = useState<string | null>(null)
  // 2.2.42 preview fix (user: "preview button kaam nahi kar raha"): slow
  // Render CPU par pehla Range request kat jata hai aur video HAMESHA frozen
  // reh jaati thi. Ab stall watchdog + saaf error/buffer messages.
  const [previewErrMsg, setPreviewErrMsg] = useState<string | null>(null)
  const [previewNote, setPreviewNote] = useState<string | null>(null)
  const [bufferStat, setBufferStat] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const stallRef = useRef({ last: -1, since: 0, retries: 0 })

  const { data: videos, isLoading } = useQuery({
    queryKey: ['videos', selectedChannel, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams()
      if (selectedChannel) params.set('channel_id', selectedChannel)
      if (statusFilter) params.set('status', statusFilter)
      const qs = params.toString()
      return api.get<Video[]>(qs ? `/videos?${qs}` : '/videos')
    },
  })

  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<{ id: string; title: string }[]>('/channels'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/videos/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['videos'] }),
    onError: (e: any) => alert(`Delete nahi hui: ${e.message}`),
  })

  const confirmDelete = (video: Video) => {
    if (window.confirm(`"${video.title}" delete karo? Live job wali video delete nahi hogi (pehle job khatam karo).`)) {
      deleteMutation.mutate(video.id)
    }
  }

  const publishMutation = useMutation({
    mutationFn: (video: Video) => api.post(`/videos/${video.id}/publish`, {
      // Unlisted-first: pehle unlisted upload, phir scheduler publish karega.
      privacy: 'unlisted',
      auto_publish_hours: 24,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      alert('Upload job shuru ho gayi — Jobs page pe progress dekho.')
    },
    onError: (e: any) => alert(`Publish nahi hui: ${e.message}`),
  })

  const syncMutation = useMutation({
    mutationFn: (video: Video) => api.post(`/videos/${video.id}/push-metadata`, {}),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['videos'] })
      alert(`YouTube par sync ho gaya: "${data?.title ?? ''}"`)
    },
    onError: (e: any) => alert(`Sync nahi hua: ${e.message}`),
  })

  // Review pending state: compliance pass + YouTube par gaya nahi + file
  // disk par hai — tabhi Approve/Reject ka matlab hai.
  const pendingReview = (v: Video) =>
    v.status === 'compliance_passed' && !v.youtube_video_id && !!v.video_file_path

  const closePreview = () => {
    setPreview(null)
    setPreviewUrl(null)
    setPreviewYt(null)
    setPreviewLoading(false)
    setPreviewErrMsg(null)
    setPreviewNote(null)
    setBufferStat(null)
  }

  // 2.2.40 FIX (user: "yaha stuck hai"): purana fetch→blob = video ka
  // PURA MP4 (380MB tak) download hone tak "Video load ho rahi hai…" —
  // slow network par minutes-hours = stuck. Ab direct <video src> URL
  // (?token=JWT — video element Authorization header nahi bhej sakta,
  // backend query-auth accept karta hai) = streaming, seconds me frame.
  const previewFallback = (video: Video) => {
    const yt = video.youtube_video_id
    if (yt && !String(yt).startsWith('mock_')) {
      setPreviewUrl(null)
      setPreviewYt(yt)
    } else {
      // 2.2.42: pehle yahan modal CHUP-CHAAP band ho jata tha = "button
      // kaam nahi kar raha". Ab modal khula rehta hai + saaf message.
      setPreviewErrMsg(
        'Video file stream nahi hui (deploy wipe ya render adhuri) — ye video dobara render karni padegi.'
      )
    }
    setPreviewLoading(false)
  }

  const openPreview = (video: Video) => {
    const token = localStorage.getItem('access_token')
    const base = (import.meta as any).env?.VITE_API_URL || '/api/v1'
    setPreview(video)
    setPreviewUrl(`${base}/videos/${video.id}/file${token ? `?token=${encodeURIComponent(token)}` : ''}`)
    setPreviewYt(null)
    setPreviewLoading(true)
    setPreviewErrMsg(null)
    setPreviewNote(null)
    setBufferStat(null)
  }

  // 2.2.42 stall watchdog: agar 9 second me na buffer aur na currentTime
  // aage badha (Range request kat gayi = ERR_ABORTED, video hamesha frozen)
  // to src dobara load karo (max 4 baar) + progress dikhao. Yahi user ka
  // "preview button kaam nahi kar raha" tha.
  useEffect(() => {
    if (!previewUrl || previewYt) return
    const st = stallRef.current
    st.last = -1
    st.since = 0
    st.retries = 0
    setPreviewNote(null)
    const id = window.setInterval(() => {
      const v = videoRef.current
      if (!v) return
      const bEnd = v.buffered.length ? v.buffered.end(v.buffered.length - 1) : 0
      const dur = Number.isFinite(v.duration) ? v.duration : 0
      setBufferStat(dur ? `Buffer: ${Math.floor(bEnd)}s / ${Math.floor(dur)}s` : null)
      const metric = Math.max(bEnd, v.currentTime)
      if (metric > st.last + 0.05) {
        st.last = metric
        st.since = Date.now()
        if (previewNote) setPreviewNote(null)
        return
      }
      if (!st.since) {
        st.since = Date.now()
        return
      }
      if (Date.now() - st.since < 9000) return
      if (st.retries >= 4) {
        setPreviewNote(
          'Stream ruk gaya (Render load zyada) — band karke dobara kholo, ya thodi der baad try karo.'
        )
        window.clearInterval(id)
        return
      }
      st.retries += 1
      st.since = Date.now()
      st.last = -1
      setPreviewNote(`Buffer ${Math.floor(bEnd)}s par atka — reconnect ${st.retries}/4…`)
      const t = v.currentTime
      try {
        v.load()
        v.addEventListener(
          'loadedmetadata',
          () => {
            try {
              v.currentTime = t
            } catch {
              /* seek best-effort */
            }
            v.play().catch(() => {})
          },
          { once: true }
        )
        v.play().catch(() => {})
      } catch {
        /* ignore */
      }
    }, 3000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl, previewYt])

  // Approve = publish endpoint (review_required gate review_approved se
  // bypass) — server wahi fixed 19:00 IST slot lagayega jo pipeline me.
  const approveMutation = useMutation({
    mutationFn: (video: Video) =>
      api.post(`/videos/${video.id}/publish`, { privacy: 'unlisted' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      alert('Approved — unlisted upload shuru, scheduler 19:00 IST slot par public karega.')
    },
    onError: (e: any) => alert(`Approve nahi hua: ${e.message}`),
  })

  // Reject = re-render: naye (fixed) pipeline se video dobara banegi.
  const rejectMutation = useMutation({
    mutationFn: (video: Video) =>
      api.post<{ job_id: string }>(`/videos/${video.id}/rerender`, { delete_youtube: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      alert('Reject: re-render job shuru — nayi visuals + acchi quality se video banegi.')
    },
    onError: (e: any) => alert(`Reject nahi hua: ${e.message}`),
  })

  const canPublish = (v: Video) =>
    !v.youtube_video_id && ['compliance_passed', 'assembled', 'metadata_ready', 'visuals_generated'].includes(v.status)

  const downloadAudio = async (id: string, lang: string) => {
    const token = localStorage.getItem('access_token')
    const base = (import.meta as any).env?.VITE_API_URL || '/api/v1'
    const res = await fetch(`${base}/videos/${id}/audio`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new Error('Audio download failed')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `voiceover_${lang}_${id}.mp3`
    a.click()
    URL.revokeObjectURL(url)
  }

  const pipelineMutation = useMutation({
    mutationFn: (video: Video) => api.post(`/jobs/run-full-pipeline`, { channel_id: video.channel_id, video_id: video.id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['videos'] }),
    onError: (e: any) => alert(`Pipeline start nahi hui: ${e.message}`),
  })

  if (isLoading) return <HudLoader text="Loading videos" />

  const getStatusBadge = (status: string) => (
    <span className={`badge ${STATUS_COLORS[status] || 'badge-secondary'}`}>
      {status.replace('_', ' ')}
    </span>
  )

  const getVisibilityBadge = (visibility: string) => (
    <span className={`badge ${VISIBILITY_COLORS[visibility] || 'badge-secondary'}`}>
      {visibility}
    </span>
  )

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Videos</h1>
          <p>Manage your video pipeline from script to publish</p>
        </div>
        <div className="header-filters">
          <select
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            className="select"
            style={{ minWidth: 200 }}
          >
            <option value="">All Channels</option>
            {channels?.map((c: any) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="select"
            style={{ minWidth: 180 }}
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="scripted">Scripted</option>
            <option value="voice_generated">Voice Generated</option>
            <option value="visuals_generated">Visuals Generated</option>
            <option value="assembled">Assembled</option>
            <option value="metadata_ready">Metadata Ready</option>
            <option value="compliance_passed">Compliance Passed</option>
            <option value="compliance_failed">Compliance Failed</option>
            <option value="scheduled">Scheduled</option>
            <option value="published">Published</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Video</th>
                <th>Channel</th>
                <th>Status</th>
                <th>SEO</th>
                <th>Visibility</th>
                <th>Language</th>
                <th>Duration</th>
                <th>YouTube ID</th>
                <th>Created</th>
                <th>Scheduled</th>
                <th>Published</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {videos?.map((video) => (
                <tr key={video.id}>
                  <td>
                    <div>
                      <span className="video-title">{video.title}</span>
                      {video.description && <span className="video-desc">{video.description.substring(0, 80)}...</span>}
                    </div>
                  </td>
                  <td>
                    <div>
                      <span className="topic-title">{channelName(channels, video.channel_id)}</span>
                      <span className="topic-desc">{video.channel_id.slice(0, 8)}…</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      {getStatusBadge(video.status)}
                      {pendingReview(video) && (
                        <span className="badge badge-warning" title="Compliance pass — preview dekh kar Approve/Reject karo">
                          Review pending
                        </span>
                      )}
                    </div>
                  </td>
                  <td title={(video.seo_issues || []).join('\n') || 'Sab checks pass'}>
                    <span className={`badge ${(video.seo_score ?? 0) >= 80 ? 'badge-success' : (video.seo_score ?? 0) >= 60 ? 'badge-warning' : 'badge-danger'}`}>
                      {video.seo_score ?? '-'}
                    </span>
                  </td>
                  <td>{getVisibilityBadge(video.visibility)}</td>
                  <td>{video.default_language}</td>
                  <td>{video.duration_seconds ? `${Math.floor(video.duration_seconds / 60)}:${String(video.duration_seconds % 60).padStart(2, '0')}` : '-'}</td>
                  <td>{video.youtube_video_id ? (
                    <a href={`https://youtube.com/watch?v=${video.youtube_video_id}`} target="_blank" rel="noopener noreferrer" className="youtube-link">
                      {video.youtube_video_id}
                    </a>
                  ) : '-'}</td>
                  <td>{video.created_at ? format(new Date(video.created_at), 'MMM dd, HH:mm') : '-'}</td>
                  <td>{video.scheduled_at ? format(new Date(video.scheduled_at), 'MMM dd, HH:mm') : '-'}</td>
                  <td>{video.published_at ? format(new Date(video.published_at), 'MMM dd, HH:mm') : '-'}</td>
                  <td>
                    <div className="action-menu">
                      {video.video_file_path && (
                        <button className="icon-btn" onClick={() => openPreview(video)} title="Preview (upload se pehle quality check)">
                          <Eye size={16} />
                        </button>
                      )}
                      <button className="icon-btn" onClick={() => window.open(video.youtube_video_id ? `https://youtube.com/watch?v=${video.youtube_video_id}` : '#', '_blank', 'noopener,noreferrer')} title="Open on YouTube" disabled={!video.youtube_video_id}>
                        <Play size={16} />
                      </button>
                      {video.youtube_video_id && !String(video.youtube_video_id).startsWith('mock_') && (
                        <button className="icon-btn" onClick={() => syncMutation.mutate(video)} disabled={syncMutation.isPending} title="DB ka title/description/tags YouTube par push karo">
                          {syncMutation.isPending && (syncMutation.variables as Video)?.id === video.id ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                        </button>
                      )}
                      <button className="icon-btn" onClick={() => pipelineMutation.mutate(video)} disabled={pipelineMutation.isPending || video.status === 'published'} title="Run Pipeline">
                        {pipelineMutation.isPending && (pipelineMutation.variables as Video)?.id === video.id ? <Loader2 size={16} className="spin" /> : <ShieldCheck size={16} />}
                      </button>
                      {pendingReview(video) ? (
                        <>
                          <button className="icon-btn" onClick={() => approveMutation.mutate(video)} disabled={approveMutation.isPending} title="Approve — YouTube par upload (unlisted → 19:00 IST slot)">
                            {approveMutation.isPending && (approveMutation.variables as Video)?.id === video.id ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                          </button>
                          <button
                            className="icon-btn danger"
                            onClick={() => { if (window.confirm(`"${video.title}" Reject karo? Nayi video re-render hogi.`)) rejectMutation.mutate(video) }}
                            disabled={rejectMutation.isPending}
                            title="Reject — re-render (nayi visuals + quality)"
                          >
                            {rejectMutation.isPending && (rejectMutation.variables as Video)?.id === video.id ? <Loader2 size={16} className="spin" /> : <RotateCcw size={16} />}
                          </button>
                        </>
                      ) : canPublish(video) ? (
                        <button className="icon-btn" onClick={() => publishMutation.mutate(video)} disabled={publishMutation.isPending} title="Upload to YouTube now">
                          {publishMutation.isPending ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
                        </button>
                      ) : null}
                      <button className="icon-btn" onClick={() => downloadAudio(video.id, video.voice_language || video.default_language)} title="Download voiceover mp3 (dub track)">
                        <Download size={16} />
                      </button>
                      <button className="icon-btn danger" onClick={() => confirmDelete(video)} disabled={deleteMutation.isPending} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!videos?.length && (
          <div className="empty-state">
            <VideoIcon size={48} />
            <h3>No videos yet</h3>
            <p>Create videos through the pipeline or add them manually</p>
          </div>
        )}
      </div>

      {preview && (
        <div className="modal-overlay" onClick={closePreview}>
          <div className="modal" style={{ maxWidth: 860, width: '92%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Preview — {preview.title.slice(0, 60)}</h2>
              <button className="icon-btn" onClick={closePreview} title="Band karo">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {previewLoading && (
                <p style={{ textAlign: 'center', padding: '12px 0' }}>Video stream ho rahi hai…</p>
              )}
              {previewErrMsg && (
                <p
                  style={{
                    textAlign: 'center',
                    padding: '12px 0',
                    color: '#f87171',
                    fontWeight: 600,
                  }}
                >
                  {previewErrMsg}
                </p>
              )}
              {!previewErrMsg && previewNote && (
                <p style={{ textAlign: 'center', padding: '6px 0', color: '#fbbf24' }}>{previewNote}</p>
              )}
              {/* 2.2.40: element loading ke DOURAN bhi mount — tabhi
                  onLoadedMetadata/onError events milenge. Streaming = seek
                  native player khud karega (Range supported).
                  2.2.42: autoPlay+muted = click karte hi frame chalu (frozen
                  black screen kabhi nahi); watchdog reconnect karta hai. */}
              {previewUrl && !previewYt && (
                <video
                  ref={videoRef}
                  src={previewUrl}
                  controls
                  autoPlay
                  muted
                  playsInline
                  style={{ width: '100%', maxHeight: '60vh', background: '#000', borderRadius: 8 }}
                  onLoadedMetadata={() => setPreviewLoading(false)}
                  onCanPlay={() => setPreviewLoading(false)}
                  onError={() => {
                    if (preview) previewFallback(preview)
                  }}
                />
              )}
              {!previewErrMsg && previewUrl && !previewYt && (
                <p className="form-help" style={{ marginTop: 6 }}>
                  Video chup-chaap shuru hoti hai — awaaz (voiceover) check karne ke liye player
                  me speaker icon dabao.{bufferStat ? ` · ${bufferStat}` : ''}
                </p>
              )}
              {!previewLoading && !previewUrl && previewYt && (
                <iframe
                  src={`https://www.youtube.com/embed/${previewYt}`}
                  title="YouTube preview"
                  style={{ width: '100%', aspectRatio: '16 / 9', maxHeight: '60vh', background: '#000', borderRadius: 8, border: 'none' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
              {!previewLoading && !previewUrl && previewYt && (
                <p className="form-help" style={{ marginTop: 8 }}>
                  Local file deploy wipe ho chuki — YouTube par uploaded video dikh rahi hai.
                </p>
              )}
              {pendingReview(preview) ? (
                <>
                  <p className="form-help" style={{ marginTop: 12 }}>
                    Quality theek lagti hai to Approve (unlisted upload → 19:00 IST slot par public),
                    kharab ho to Reject (re-render — nayi visuals + quality).
                  </p>
                  <div className="modal-actions">
                    <button className="btn btn-primary" onClick={() => { closePreview(); approveMutation.mutate(preview) }}>
                      <Check size={16} /> Approve — upload karo
                    </button>
                    <button className="btn btn-secondary" onClick={() => { closePreview(); rejectMutation.mutate(preview) }}>
                      <RotateCcw size={16} /> Reject — re-render
                    </button>
                  </div>
                </>
              ) : (
                <p className="form-help" style={{ marginTop: 12 }}>
                  {preview.youtube_video_id
                    ? 'YouTube par uploaded — sirf quality check ke liye preview.'
                    : 'Sirf quality check ke liye preview (approve/reject sirf Review pending videos par).'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}